import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../../components/ui/accordion.js';
import { Badge } from '../../../../components/ui/badge.js';
import type { WorkfrontCustomFormParameter } from '../types.js';

interface CustomFormGroupParamProps {
  parameter: WorkfrontCustomFormParameter;
}

/**
 * Per-parameter accordion inside the preview panel. Verbatim port of
 * `wf-audit/.../CustomFormGroupParam.tsx` — surfaces every Workfront
 * parameter attribute the operator cares about while reviewing a clone
 * or replace. The conditional render blocks below mirror Workfront's
 * `displayType` taxonomy (EXTRNL/MULTEXTRNL, SLCT/MULT/CHCK/RDIO,
 * WFNATIVE, TYAH, IMAGE, CALC, UIEXTNSION, WFPLANNING).
 */
export function CustomFormGroupParam({ parameter }: CustomFormGroupParamProps): JSX.Element {
  return (
    <Accordion type="single" collapsible className="max-w-full">
      <AccordionItem value="item">
        <AccordionTrigger className="text-foreground/85 hover:no-underline">
          <span className="font-bold">DE:{parameter.name}</span>
          {parameter.isRequired ? (
            <Badge className="ml-2" variant="default">
              required
            </Badge>
          ) : null}
        </AccordionTrigger>

        <AccordionContent className="space-y-1 pb-6 pl-6 text-sm text-foreground/75">
          <p>
            <b>Label:</b> {parameter.label}
          </p>
          {parameter.description && (
            <p>
              <b>Description:</b> {parameter.description}
            </p>
          )}
          <p>
            <b>Data type:</b> {parameter.dataType}
          </p>
          <p>
            <b>Display type:</b> {parameter.displayType}
          </p>
          {parameter.format && (
            <p>
              <b>Format:</b> {parameter.format}
            </p>
          )}
          {parameter.length != null && (
            <p>
              <b>Length:</b> {parameter.length}
            </p>
          )}
          {parameter.referenceObjCode && (
            <p>
              <b>Reference object code:</b> {parameter.referenceObjCode}
            </p>
          )}
          {parameter.multiValue != null && (
            <p>
              <b>Multi-value:</b> {String(parameter.multiValue)}
            </p>
          )}
          {parameter.validationExpression && (
            <p>
              <b>Validation expression:</b> {parameter.validationExpression}
            </p>
          )}
          {parameter.isEncrypted != null && (
            <p>
              <b>Encrypted:</b> {String(parameter.isEncrypted)}
            </p>
          )}
          {parameter.calculatedExpression && (
            <p>
              <b>Calculated expression:</b> {parameter.calculatedExpression}
            </p>
          )}
          {parameter.isReadOnly != null && (
            <p>
              <b>Read-only:</b> {String(parameter.isReadOnly)}
            </p>
          )}
          {parameter.isVisible != null && (
            <p>
              <b>Visible:</b> {String(parameter.isVisible)}
            </p>
          )}
          {parameter.defaultValue && (
            <p>
              <b>Default value:</b> {parameter.defaultValue}
            </p>
          )}

          {['EXTRNL', 'MULTEXTRNL'].includes(parameter.displayType || '') && (
            <>
              <p>
                <b>Http method:</b> {parameter.fieldDefinition?.httpMethod}
              </p>
              <p>
                <b>Json path:</b> {parameter.fieldDefinition?.jsonPath}
              </p>
              <p>
                <b>Link:</b> {parameter.fieldDefinition?.link}
              </p>
            </>
          )}

          {['SLCT', 'MULT', 'CHCK', 'RDIO'].includes(parameter.displayType || '') && (
            <>
              <p>
                <b>Options:</b>
              </p>
              <ul className="ml-5 list-disc">
                {(parameter.parameterOptions || []).map((option) => (
                  <li key={option.ID} className="mb-2">
                    <b>Label</b>: <i>{option.label}</i>
                    <br />
                    <b>Value</b>: <i>{option.value}</i>
                    <br />
                    <b>Is default</b>: <i>{String(option.isDefault)}</i>
                    <br />
                    <b>Is hidden</b>: <i>{String(option.isHidden)}</i>
                    <br />
                    <b>Display order</b>: <i>{option.displayOrder}</i>
                  </li>
                ))}
              </ul>
            </>
          )}

          {['WFNATIVE'].includes(parameter.displayType || '') && (
            <p>
              <b>Reference field:</b> {parameter.fieldDefinition?.referenceField?.toUpperCase()}
            </p>
          )}

          {['TYAH'].includes(parameter.displayType || '') && (
            <p>
              <b>Reference object code:</b> {parameter.refObjCode}
            </p>
          )}

          {['IMAGE'].includes(parameter.displayType || '') && (
            <p>
              <b>Url:</b> {parameter.fieldDefinition?.url}
            </p>
          )}

          {['CALC'].includes(parameter.displayType || '') && (
            <>
              <p>
                <b>Custom expression:</b> {parameter.categoryParameterExpression?.customExpression}
              </p>
              <p>
                <b>Has finance fields:</b>{' '}
                {String(parameter.categoryParameterExpression?.hasFinanceFields)}
              </p>
            </>
          )}

          {['UIEXTNSION'].includes(parameter.displayType || '') && (
            <p>
              <b>Extension:</b> {parameter.fieldDefinition?.extension}
            </p>
          )}

          {['WFPLANNING'].includes(parameter.displayType || '') && (
            <p>
              <b>Workfront object type:</b> {parameter.fieldDefinition?.wfObjectType}
            </p>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
